/**
 * Truy cập dữ liệu của module eKYC (Định danh eKYC).
 *
 * Gọi API thật khi đăng nhập Keycloak thật, dùng `src/mock/ekyc.ts` khi chưa (`dungApiThat()`).
 *
 * ## Đường dẫn theo quy ước của kho này, không theo v3
 *
 * `/api/v1/m/ekyc/...` — route module chuẩn của APISIX, như `tham-dinh` và `tri-thuc`. Mã `ekyc` là
 * **ràng buộc, không phải lựa chọn**: nó đã nằm trong `vung_api` của dịch vụ `dinh-danh-ekyc`
 * (`db/control-plane/migrations/V2__goi_dich_vu.sql` dòng 178) và migration đó **đã chạy**.
 *
 * ⚠️ Bộ bàn giao `docs/eKYC/02_API.md` §1 tả đường dẫn `/api/ekyc/...` với hai cây `/v1/*` (đối tác
 * tự xác thực) và phần còn lại (JWT nội bộ). Đó là hình dạng của **gateway NestJS ở v3** và **không
 * áp dụng ở đây**: APISIX chỉ định tuyến `/api/v1/m/{code}`, nên `/api/ekyc` không khớp route nào
 * và trả **404** — đã gặp thật trên bản deploy.
 *
 * Xác thực đối tác **không** dựng lại như v3 (access token riêng, HMAC, `clientToken`): đối tác gọi cùng bộ
 * endpoint này bằng khoá API của tenant, Gateway kiểm `X-Api-Key` (ADR-0068, ADR-0085 mục 1). Tệp này phục vụ
 * giao diện quản trị; giao diện **không bao giờ** gửi khoá API.
 *
 * ## Đường dẫn tiếng Việt, payload camelCase — lệch có chủ ý
 *
 * Endpoint và tham số query đi theo quy ước kho (`/ho-so`, `/thong-ke`, `?trang-thai=`), như
 * `tham-dinh` và `tri-thuc`. Nhưng **thân phản hồi giữ camelCase tiếng Anh** của bộ bàn giao: đó là
 * hợp đồng đối tác đã `switch` lên (`02` §3.3), và các mã `RC_*`/`HR-*`/`E_*` trong đó là hợp đồng
 * với cả cơ quan quản lý. Đổi đường dẫn là việc nội bộ; đổi thân là đổi hợp đồng.
 *
 * Backend ở `modules/ekyc/backend`; ánh xạ snake_case → camelCase nằm ở `ho-so/ra-json.ts` của nó, và
 * `ra-json.test.ts` đọc thẳng `modules/ekyc/types.ts` của web để hai bên không trôi khỏi nhau. Cấu hình (quy trình,
 * ứng dụng, webhook) nằm ở `ekyc-cau-hinh.repository.ts`.
 *
 * ## Hai lời gọi để lại vết
 *
 * `thongTinGiayTo()` và `anh()` **mỗi lần gọi ghi một dòng nhật ký kèm danh tính người xem**
 * (`02` §7.1). Đó là thứ duy nhất phân biệt truy cập hợp pháp với tò mò về dữ liệu người khác —
 * nên hai hàm này **tuyệt đối không được bọc trong `useQuery` có refetch nền**: nền sẽ **bịa ra
 * những dòng "đã xem ảnh"** mà không ai bấm gì (`06` §3.5).
 */
import { dungApiThat, goiApi, goiApiTep } from '../api/http';
import { mayChuEkyc } from '../../mock/ekyc';
import { mockRequest, mockState } from '../../mock/transport';
import type {
  AnhDaTai,
  DongNhatKy,
  PhienChiTiet,
  PhienTomTat,
  ThamDinhGui,
  ThongKeEkyc,
  ThongTinGiayTo,
  ToanVenNhatKy,
  Trang,
  TrangThaiPhien,
  TrienKhai,
  VaiTroTaiLen,
  VaiTroTep,
} from '../../modules/ekyc/types';

/** Route module của APISIX; Gateway chuyển **nguyên đường dẫn** tới module (ADR-0051 mục 2). */
const GOC = '/api/v1/m/ekyc';

const ma = (id: string) => encodeURIComponent(id);

/**
 * Chạy máy chủ giả **sau** nhịp chờ và các trạng thái `?mock=` của `mockRequest`: lỗi nghiệp vụ
 * tới cùng độ trễ với lỗi thật.
 */
const giaLap = <T>(lam: () => T): Promise<T> => mockRequest(undefined).then(lam);

/** Trang rỗng cho `?mock=empty` và `?mock=filtered-empty`. */
function trangRong<T>(): Trang<T> {
  return { items: [], next_cursor: null };
}

/** Số liệu rỗng: mọi con số về 0, và `autoRate`/`avgMs` là `null` — chưa hồ sơ nào xong. */
function thongKeRong(): ThongKeEkyc {
  return { total: 0, pending: 0, autoRate: null, avgMs: null, byStatus: {}, topReasons: [] };
}

export interface ThamSoDanhSach {
  limit: number;
  cursor?: string;
  /** Tab đã gộp thành danh sách trạng thái; `undefined` là không lọc. */
  trangThai?: readonly TrangThaiPhien[];
  /** Chỉ khớp `referenceId` (`02` §7.1) — không tìm theo tên hay số giấy tờ. */
  search?: string;
}

export const ekycRepository = {
  /** Số liệu 30 ngày gần nhất của màn Tổng quan. */
  async thongKe(days: number): Promise<ThongKeEkyc> {
    if (!dungApiThat()) return mockRequest(mayChuEkyc.thongKe(), thongKeRong());
    return goiApi<ThongKeEkyc>(`${GOC}/thong-ke`, { query: { ngay: days } });
  },

  /**
   * Chế độ triển khai — thẻ "Kết nối của service định danh".
   *
   * Cố ý **không** nằm trên `/health` (chỉ cần đăng nhập) vì "ai kiểm phân quyền" là một tấm bản
   * đồ chỉ thẳng vào chỗ yếu (`02` §7.2).
   */
  async trienKhai(): Promise<TrienKhai> {
    if (!dungApiThat()) return giaLap(() => mayChuEkyc.trienKhai());
    return goiApi<TrienKhai>(`${GOC}/trien-khai`);
  },

  /**
   * Kiểm toàn vẹn chuỗi băm nhật ký.
   *
   * ⚠️ Ở v3 đường tương ứng **luôn 403**: portal gọi `GET /api/ekyc/audit/verify` nhưng
   * `ekyc-permission.ts` của gateway **không có nhánh `audit`**, nên `quyenCanCo` trả `null` và
   * guard từ chối (`00` §8 lỗi #1, `08` §4 việc #1). Màn phải dựng nhánh "chưa kiểm được" tử tế —
   * một lỗi phân quyền **không** được đọc thành một cáo buộc giả mạo dữ liệu.
   */
  async toanVenNhatKy(): Promise<ToanVenNhatKy> {
    if (!dungApiThat()) return giaLap(() => mayChuEkyc.toanVen());
    return goiApi<ToanVenNhatKy>(`${GOC}/nhat-ky/kiem`);
  },

  /** `GET /ho-so` — ⚠️ đọc **xuyên tenant**, mọi app. Endpoint nhạy nhất của cả phân hệ. */
  async danhSach(thamSo: ThamSoDanhSach): Promise<Trang<PhienTomTat>> {
    if (!dungApiThat()) {
      /*
       * `filtered-empty` phải **có bộ lọc đang bật** mới diễn được: trạng thái rỗng vì bộ lọc bỏ
       * cả mô tả lẫn nút hành động, còn rỗng thật thì mời tạo cái đầu tiên. Ở màn này "bộ lọc"
       * gồm cả tab trạng thái, không chỉ ô tìm.
       */
      const coLoc = Boolean(thamSo.search) || thamSo.trangThai !== undefined;
      if (mockState() === 'filtered-empty' && coLoc) return mockRequest(trangRong<PhienTomTat>());
      return mockRequest(mayChuEkyc.danhSach(thamSo.trangThai, thamSo.search ?? ''), trangRong<PhienTomTat>());
    }
    return goiApi<Trang<PhienTomTat>>(`${GOC}/ho-so`, {
      query: {
        limit: thamSo.limit,
        cursor: thamSo.cursor,
        'trang-thai': thamSo.trangThai?.join(','),
        search: thamSo.search,
      },
    });
  },

  async chiTiet(id: string): Promise<PhienChiTiet> {
    if (!dungApiThat()) return giaLap(() => mayChuEkyc.chiTiet(id));
    return goiApi<PhienChiTiet>(`${GOC}/ho-so/${ma(id)}`);
  },

  /**
   * Nhật ký của một phiên.
   *
   * Đường này cố ý **bỏ** `documentInfo` và đường dẫn ảnh (`02` §7.1): muốn thấy dữ liệu thô thì
   * phải **mở ảnh**, và việc đó để lại vết.
   */
  async nhatKy(id: string): Promise<DongNhatKy[]> {
    if (!dungApiThat()) return giaLap(() => mayChuEkyc.nhatKy(id));
    return goiApi<DongNhatKy[]>(`${GOC}/ho-so/${ma(id)}/nhat-ky`);
  },

  /** ⚠️ Mở dữ liệu cá nhân — **ghi nhật ký kèm danh tính người xem**. Gọi theo yêu cầu, không nền. */
  async thongTinGiayTo(id: string): Promise<ThongTinGiayTo | null> {
    if (!dungApiThat()) return giaLap(() => mayChuEkyc.thongTinGiayTo(id));
    return goiApi<ThongTinGiayTo | null>(`${GOC}/ho-so/${ma(id)}/giay-to`);
  },

  /**
   * ⚠️ Mở ảnh mặt người — **ghi nhật ký kèm danh tính người xem**.
   *
   * Ảnh stream **qua API**, không bao giờ qua presigned URL MinIO (`02` §7.1): một URL presigned
   * rời khỏi hệ thống là không kiểm soát được (dán vào chat, vào ticket) và không ai ghi lại ai đã xem.
   */
  async anh(id: string, vaiTro: VaiTroTep): Promise<Blob> {
    if (!dungApiThat()) return giaLap(() => mayChuEkyc.anh(id, vaiTro));
    return goiApiTep(`${GOC}/ho-so/${ma(id)}/anh/${ma(vaiTro)}`, 'image/*, application/problem+json');
  },

  // ----- Ghi: luồng định danh tại quầy ------------------------------------------------------------------------

  /** Mở phiên. Phiên dừng ở `CONSENT_REQUIRED`: đồng ý là hành vi của người dùng cuối, không ai làm hộ. */
  async taoPhien(body: {
    documentType: string; referenceId: string; ungDungId?: string; workflowId?: string;
  }): Promise<PhienTomTat> {
    if (!dungApiThat()) return giaLap(() => mayChuEkyc.taoPhien(body.documentType, body.referenceId));
    return goiApi<PhienTomTat>(`${GOC}/ho-so`, { method: 'POST', body });
  },

  /**
   * Ghi lượt đồng ý **hoặc từ chối** của khách. `granted: false` không phải lỗi — nó cũng được ghi lại, vì
   * "người này đã từng được hỏi chưa" là câu hỏi sẽ được đặt khi có khiếu nại.
   */
  async dongY(id: string, granted: boolean, termsVersion: string): Promise<PhienTomTat> {
    if (!dungApiThat()) return giaLap(() => mayChuEkyc.dongY(id, granted));
    return goiApi<PhienTomTat>(`${GOC}/ho-so/${ma(id)}/consent`, {
      method: 'POST', body: { granted, termsVersion, purposes: ['dinh-danh'] },
    });
  },

  /** Tải một ảnh lên. Ảnh qua cổng kiểm của server (định dạng, kích thước); lỗi mang câu nói cách xử lý. */
  async taiAnh(vaiTro: VaiTroTaiLen, tep: File): Promise<AnhDaTai> {
    if (!dungApiThat()) return giaLap(() => mayChuEkyc.taiAnh(vaiTro, tep.size));
    const bieuMau = new FormData();
    bieuMau.append('vaiTro', vaiTro);
    bieuMau.append('file', tep);
    return goiApi<AnhDaTai>(`${GOC}/tep`, { method: 'POST', bieuMau });
  },

  async ganAnh(id: string, fileId: string, role: VaiTroTaiLen): Promise<PhienTomTat> {
    if (!dungApiThat()) return giaLap(() => mayChuEkyc.ganAnh(id, role));
    return goiApi<PhienTomTat>(`${GOC}/ho-so/${ma(id)}/tep`, { method: 'POST', body: { fileId, role } });
  },

  async nop(id: string): Promise<PhienTomTat> {
    if (!dungApiThat()) return giaLap(() => mayChuEkyc.nop(id));
    return goiApi<PhienTomTat>(`${GOC}/ho-so/${ma(id)}/nop`, { method: 'POST' });
  },

  /** Quyết định của người thẩm định trên hồ sơ đang chờ người (`EKYC_REVIEW.UPDATE`). */
  async thamDinh(id: string, du: ThamDinhGui): Promise<PhienTomTat> {
    if (!dungApiThat()) return giaLap(() => mayChuEkyc.thamDinh(id, du));
    return goiApi<PhienTomTat>(`${GOC}/ho-so/${ma(id)}/tham-dinh`, { method: 'POST', body: du });
  },

  async huy(id: string): Promise<PhienTomTat> {
    if (!dungApiThat()) return giaLap(() => mayChuEkyc.huy(id));
    return goiApi<PhienTomTat>(`${GOC}/ho-so/${ma(id)}/huy`, { method: 'POST' });
  },

  /**
   * Xoá dữ liệu cá nhân của một hồ sơ (`DELETE /ho-so/:id`, `204`). Dòng hồ sơ **ở lại** với trạng thái `DELETED`,
   * cùng bản ghi đồng ý và nhật ký — bằng chứng rằng hồ sơ đã tồn tại và đã được xoá lúc nào.
   */
  async xoa(id: string): Promise<void> {
    if (!dungApiThat()) return giaLap(() => mayChuEkyc.xoa(id));
    await goiApi<void>(`${GOC}/ho-so/${ma(id)}`, { method: 'DELETE' });
  },
};
